// src/context/AuthContext.js
import React, { createContext, useState, useEffect } from 'react';
import { isTokenExpired, clearAuthData, setupAuthInterceptor } from '../utils/authHelper';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState({});
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Function to handle logout
  const logout = () => {
    console.log('Logging out user...');
    clearAuthData();
    setIsAuthenticated(false);
    setUser({});
    setToken(null);
    
    // Redirect to login page
    window.location.href = '/login';
  };

  // Login function
  const login = (userData, authToken) => {
    setUser(userData);
    setToken(authToken);
    setIsAuthenticated(true);
    localStorage.setItem('token', authToken);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  // Set up auth state from localStorage on initial load
  useEffect(() => {
    const initAuth = () => {
      const storedToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      
      if (storedToken && storedUser && !isTokenExpired()) {
        // Valid token exists
        setToken(storedToken);
        setUser(JSON.parse(storedUser));
        setIsAuthenticated(true);
      } else if (storedToken && isTokenExpired()) {
        // Token exists but is expired
        logout();
      }
      
      setLoading(false);
    };
    
    initAuth();
    
    // Set up interceptor for handling auth
    setupAuthInterceptor(logout);
    
    // Set up interval to check token expiration every minute
    const interval = setInterval(() => {
      if (isTokenExpired() && isAuthenticated) {
        logout();
      }
    }, 60000);
    
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  // Update user data in localStorage whenever it changes
  useEffect(() => {
    if (user && Object.keys(user).length > 0) {
      localStorage.setItem('user', JSON.stringify(user));
    }
  }, [user]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        setIsAuthenticated,
        user,
        setUser,
        token,
        setToken,
        login,
        logout,
        loading
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
